import { Children, useId } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CitedSuggestion } from './AssistantSuggestionApply';

// Route unfenced structured replies through the same safe display renderer.
function structuredParagraphs() {
  return tree => {
    function visit(node) {
      if (!node.children) return;
      node.children = node.children.map(child => {
        if (child.type === 'paragraph' && child.children.every(part => part.type === 'text')) {
          const value = child.children.map(part => part.value).join('');
          if (/^\s*"?[\[{]/.test(value)) return { type: 'code', lang: 'json', value };
        }
        visit(child);
        return child;
      });
    }
    visit(tree);
  };
}
const plugins = [remarkGfm, structuredParagraphs];
const number = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const axisNumber = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });
const text = value => typeof value === 'string' && value.trim().length > 0 && value.length <= 500;
// A citation of a stored analysis suggestion. It names the suggestion only; the
// panel renders an Apply control solely for citations the server validated.
const citation = value => Boolean(value) && typeof value === 'object' && typeof value.messageId === 'string'
  && /^[A-Za-z0-9_-]{1,100}$/.test(value.messageId) && Number.isInteger(value.index) && value.index >= 0 && value.index < 1000;
const valueLabel = (value, unit) => `${number.format(value)}${unit === '%' ? '%' : unit ? ` ${unit}` : ''}`;

export function Trend({ title, unit = '', points:sourcePoints, grain }) {
  const dated=sourcePoints.every(point=>/^\d{4}-\d{2}-\d{2}$/.test(point.label)&&Number.isFinite(Date.parse(point.label)));
  const points=dated?[...sourcePoints].sort((a,b)=>a.label.localeCompare(b.label)):sourcePoints;
  const dates=dated?points.map(point=>Date.parse(point.label)):[];
  const timed=dated&&dates.at(-1)>dates[0];
  const descriptionId = useId();
  const values = points.map(point => point.value);
  const low = Math.min(...values), high = Math.max(...values);
  const padding = high === low ? Math.max(Math.abs(high) * 0.1, 1) : (high - low) * 0.12;
  const min = low - padding, max = high + padding;
  const x = index => 76 + (timed?(dates[index]-dates[0])/(dates.at(-1)-dates[0]):index/(points.length-1))*488;

  const y = value => 154 - (value - min) * 116 / (max - min);
  const lines=points.reduce((groups,point,index)=>{if(!groups.length||(timed&&index>0&&dates[index]-dates[index-1]>(grain==='weekly'?7:1)*86400000))groups.push([]);groups.at(-1).push(`${x(index)},${y(point.value)}`);return groups;},[]);
  const labelsEvery = Math.max(1, Math.ceil((points.length - 1) / 4));
  return <figure className="ow-assistant-trend">
    <figcaption>{title}{unit && <span>{unit}</span>}</figcaption>
    <div className="ow-assistant-trend-plot"><svg viewBox="0 0 640 204" role="img" aria-label={title} aria-describedby={descriptionId}>
      <desc id={descriptionId}>{points.map(point => `${point.label}: ${valueLabel(point.value, unit)}`).join('; ')}</desc>
      {[min, (min + max) / 2, max].map((tick, index) => <g key={index}>
        <line x1="76" x2="564" y1={y(tick)} y2={y(tick)} className="ow-trend-grid" />
        <text x="66" y={y(tick) + 4} textAnchor="end">{axisNumber.format(tick)}</text>
      </g>)}
      {lines.map((line,index)=><polyline key={index} points={line.join(' ')} fill="none" className="ow-trend-line" />)}
      {points.map((point, index) => <g key={index}>
        <circle cx={x(index)} cy={y(point.value)} r="3.5" className="ow-trend-point"><title>{point.label}: {valueLabel(point.value, unit)}</title></circle>
        {(index % labelsEvery === 0 || index === points.length - 1) && <text x={x(index)} y="182" textAnchor="middle">{point.label.length > 16 ? `${point.label.slice(0, 15)}…` : point.label}</text>}
      </g>)}
    </svg></div>
    <details className="ow-assistant-data"><summary>View data</summary>
      <div className="ow-assistant-table"><table><thead><tr><th scope="col">Period</th><th scope="col">{unit || 'Value'}</th></tr></thead>
        <tbody>{points.map((point, index) => <tr key={index}><th scope="row">{point.label}</th><td>{valueLabel(point.value, unit)}</td></tr>)}</tbody></table></div>
    </details>
  </figure>;
}

export function AdvisoryActions({ items }) {
  return <section className="ow-assistant-actions" aria-label="Recommended actions">
    <header><h4>Recommended actions</h4>{!items.some(item => item.suggestion) && <span className="ow-assistant-advisory">Advisory</span>}</header>
    <ol>{items.map((item, index) => <li key={index}><strong>{item.title}</strong>
      {item.reason && <p>{item.reason}</p>}{item.check && <p className="ow-assistant-action-check"><b>Check:</b> {item.check}</p>}
      {item.suggestion && <CitedSuggestion suggestion={item.suggestion} />}
    </li>)}</ol>
  </section>;
}

// Models sometimes emit a JSON string, or escape every quote without the
// enclosing string. Recover only by JSON parsing; never evaluate model output.
export function decodeStructuredReply(source) {
  if (source.length > 30000) return null;
  let value = source.trim();
  for (let depth = 0; depth < 4; depth += 1) {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object') return parsed;
      if (typeof parsed !== 'string' || parsed === value) return null;
      value = parsed;
    } catch {
      if (!value.includes('\\"')) return null;
      try { value = JSON.parse('"' + value.replace(/\r/g, '\\r').replace(/\n/g, '\\n').replace(/\t/g, '\\t') + '"'); }
      catch { return null; }
    }
  }
  return null;
}

// Saved answers remain Markdown; validate charts and cards before rendering.
export function parseDisplayBlock(language, source) {
  if (!['campaign-trend', 'campaign-actions'].includes(language) || source.length > 30000) return null;
  try {
    const data = decodeStructuredReply(source);
    if (!data || typeof data !== 'object') return null;
    if (language === 'campaign-trend' && text(data.title) && (data.unit === undefined || (typeof data.unit === 'string' && data.unit.length <= 30))
      && Array.isArray(data.points) && data.points.length >= 2 && data.points.length <= 60
      && data.points.every(point => point && text(point.label) && typeof point.value === 'number' && Number.isFinite(point.value) && Math.abs(point.value) <= Number.MAX_SAFE_INTEGER)) {
      return { type: 'trend', data };
    }
    if (language === 'campaign-actions' && Array.isArray(data.items) && data.items.length > 0 && data.items.length <= 20
      && data.items.every(item => item && text(item.title) && [item.reason, item.check].every(value => value === undefined || text(value)))) {
      // A malformed citation only loses its link; the advisory item still renders.
      return { type: 'actions', data: { ...data, items: data.items.map(({ suggestion, ...item }) => (citation(suggestion) ? { ...item, suggestion } : item)) } };
    }
  } catch { /* The display renderer supplies a readable fallback. */ }
  return null;
}

// A short list reads as a list; only a long one folds, after its first items, so nothing sits behind an "N items" label.
const LIST_SHOWN = 6;
export function CollapsibleList({ ordered, start, children }) {
  const List = ordered ? 'ol' : 'ul';
  const items = Children.toArray(children).filter(child => typeof child !== 'string');
  if (items.length <= LIST_SHOWN) return <List className="ow-assistant-list" start={ordered ? start : undefined}>{items}</List>;
  return <div className="ow-assistant-list"><List start={ordered ? start : undefined}>{items.slice(0, LIST_SHOWN)}</List>
    <details><summary>Show {items.length - LIST_SHOWN} more</summary><List start={ordered ? (start || 1) + LIST_SHOWN : undefined}>{items.slice(LIST_SHOWN)}</List></details></div>;
}

function ReadableData({ value, depth = 0 }) {
  if (depth > 8) return <p>Additional detail could not be displayed.</p>;
  if (value === null) return <span>Not available</span>;
  if (typeof value !== 'object') return <span>{String(value)}</span>;
  if (Array.isArray(value)) return <ul>{value.map((item, index) => <li key={index}><ReadableData value={item} depth={depth + 1} /></li>)}</ul>;
  return <dl>{Object.entries(value).map(([key, item]) => <div key={key}>
    <dt><strong>{key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ')}</strong></dt>
    <dd><ReadableData value={item} depth={depth + 1} /></dd>
  </div>)}</dl>;
}

export function DisplayBlock({ children }) {
  const code = Children.toArray(children)[0];
  const language = code?.props?.className?.replace(/^language-/, '');
  const source = String(code?.props?.children || '');
  const parsed = parseDisplayBlock(language, source)
    || parseDisplayBlock('campaign-actions', source)
    || parseDisplayBlock('campaign-trend', source);
  if (parsed?.type === 'trend') return <Trend {...parsed.data} />;
  if (parsed?.type === 'actions') return <AdvisoryActions {...parsed.data} />;
  const data = decodeStructuredReply(source);
  if (data) return <div className="ow-assistant-readable-data"><ReadableData value={data} /></div>;
  if (['campaign-actions', 'campaign-trend', 'json'].includes(language?.toLowerCase()) || /^\s*"?[\[{]/.test(source)) {
    return <p role="note">This part of the reply could not be displayed. Ask the assistant to restate it in plain language.</p>;
  }
  return <pre>{children}</pre>;
}

const components = {
  pre: DisplayBlock,
  table: ({ children }) => <div className="ow-assistant-table" tabIndex={0} role="region" aria-label="Comparison table"><table>{children}</table></div>,
  ul: ({ children }) => <CollapsibleList>{children}</CollapsibleList>,
  ol: ({ children, start }) => <CollapsibleList ordered start={start}>{children}</CollapsibleList>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
};

export default function AssistantAnswer({ answer }) {
  return <div className="ow-assistant-answer"><ReactMarkdown remarkPlugins={plugins} components={components} skipHtml>{answer}</ReactMarkdown></div>;
}
